import m from "mithril";
import Layout from "./Layout.js";
import { getMithril, showToast } from "../utils.js";

var ViewAccount = {
    oninit: function (vnode) {
        this.accountId = m.route.param("id");
        this.isLoading = false;
        this.error = null;
        this.accountData = null;

        if (this.accountId) {
            this.fetchAccountDetails();
        } else {
            showToast("ID rekening tidak ditemukan", "error");
        }
    },

    fetchAccountDetails: async function () {
        try {
            console.log("Fetching account details for ID:", this.accountId);
            const response = await getMithril(`/api/savings-accounts/${this.accountId}`, { component: this });
            console.log("API response received:", response);

            // API returns { success: true, data: accountData }
            if (response && response.success && response.data) {
                this.accountData = response.data;
                console.log("✅ Account data loaded:", this.accountData);
            } else {
                console.error("Invalid API response structure:", response);
                this.accountData = null;
                this.error = "Invalid response format from server";
            }

            this.error = null; // Clear any previous errors if we got here
        } catch (e) {
            console.error("Account loading error:", e);
            this.error = "Error memuat detail rekening: " + e.message;
            showToast("Error memuat detail rekening: " + e.message, "error");
            m.route.set("/account-list");
        } finally {
            this.isLoading = false;
            m.redraw();
        }
    },

    formatCurrency: function (amount) {
        // Handle NaN, undefined, null, or non-numeric values safely
        const numericAmount = parseFloat(amount) || 0;
        if (isNaN(numericAmount)) {
            return "Rp 0";
        }
        try {
            return new Intl.NumberFormat('id-ID', {
                style: 'currency',
                currency: 'IDR',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(numericAmount);
        } catch (e) {
            console.warn("Currency formatting error:", e);
            return `Rp ${numericAmount.toLocaleString('id-ID')}`;
        }
    },

    formatDate: function (dateString) {
        if (!dateString) return "-";
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return "-"; // Invalid date
            }
            return date.toLocaleDateString('id-ID');
        } catch (e) {
            console.warn("Date formatting error:", e);
            return "-";
        }
    },

    safeValue: function (value, fallback = "-") {
        return value || fallback;
    },

    view: function (vnode) {
        return [
            m("div", { class: "max-w-7xl mx-auto px-4 sm:px-6 md:px-8" }, [
                m("div", { class: "md:flex md:items-center md:justify-between" }, [
                    m("div", { class: "flex-1 min-w-0" }, [
                        m("h2", { class: "text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:truncate" }, 
                            "Detail Rekening Simpanan"
                        )
                    ]),
                    m("div", { class: "mt-4 flex md:mt-0 md:ml-4" }, [
                        m("button", {
                            type: "button",
                            class: "ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500",
                            onclick: () => m.route.set("/account-list")
                        }, "Kembali"),
                        m("button", {
                            type: "button",
                            class: "inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500",
                            onclick: () => m.route.set(`/edit-account/${this.accountData.id}`)
                        }, "Edit Rekening")
                    ])
                ]),

                m("div", { class: "mt-8 bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg" }, [
                    this.isLoading ? 
                        m("div", { class: "px-4 py-5 sm:p-6" }, [
                            m("div", { class: "text-center py-12" }, [
                                m("div", { class: "inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-4" }),
                                m("p", { class: "text-gray-600 dark:text-gray-400" }, "Memuat data rekening...")
                            ])
                        ]) : 
                        this.accountData ?
                            m("div", { class: "border-t border-gray-200 dark:border-gray-700" }, [
                                m("dl", [
                                    m("div", { class: "bg-gray-50 dark:bg-gray-700 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" }, [
                                        m("dt", { class: "text-sm font-medium text-gray-500 dark:text-gray-400" }, "Nomor Rekening"),
                                        m("dd", { class: "mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2" }, this.safeValue(this.accountData.account_number))
                                    ]),
                                    m("div", { class: "bg-white dark:bg-gray-800 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" }, [
                                        m("dt", { class: "text-sm font-medium text-gray-500 dark:text-gray-400" }, "Nama Rekening"),
                                        m("dd", { class: "mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2" }, this.safeValue(this.accountData.account_name))
                                    ]),
                                    m("div", { class: "bg-gray-50 dark:bg-gray-700 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" }, [
                                        m("dt", { class: "text-sm font-medium text-gray-500 dark:text-gray-400" }, "Nasabah"),
                                        m("dd", { class: "mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2" }, this.safeValue(this.accountData.customer_name))
                                    ]),
                                    m("div", { class: "bg-white dark:bg-gray-800 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" }, [
                                        m("dt", { class: "text-sm font-medium text-gray-500 dark:text-gray-400" }, "Produk Simpanan"),
                                        m("dd", { class: "mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2" }, this.safeValue(this.accountData.product_name))
                                    ]),
                                    m("div", { class: "bg-gray-50 dark:bg-gray-700 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" }, [
                                        m("dt", { class: "text-sm font-medium text-gray-500 dark:text-gray-400" }, "Saldo"),
                                        m("dd", { class: "mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2" }, this.formatCurrency(this.accountData.balance))
                                    ]),
                                    m("div", { class: "bg-white dark:bg-gray-800 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" }, [
                                        m("dt", { class: "text-sm font-medium text-gray-500 dark:text-gray-400" }, "Tanggal Pembukaan"),
                                        m("dd", { class: "mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2" }, this.formatDate(this.accountData.opening_date))
                                    ]),
                                    m("div", { class: "bg-gray-50 dark:bg-gray-700 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" }, [
                                        m("dt", { class: "text-sm font-medium text-gray-500 dark:text-gray-400" }, "Status"),
                                        m("dd", { class: "mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2" },
                                            m("span", {
                                                class: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                    this.safeValue(this.accountData.status, 'inactive').toLowerCase() === 'active'
                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                                }`
                                            }, this.safeValue(this.accountData.status, 'inactive').toLowerCase() === 'active' ? 'Aktif' : 'Tidak Aktif')
                                        )
                                    ])
                                ])
                            ]) :
                            m("div", { class: "px-4 py-5 sm:p-6" }, [
                                m("div", { class: "text-center py-12" }, [
                                    m("p", { class: "text-gray-600 dark:text-gray-400" }, "Data rekening tidak ditemukan")
                                ])
                            ])
                ])
            ])
        ];
    }
};

export default ViewAccount;